/**
 * SanPlumb — TopBar (Fase 3b)
 *
 * Barra superior con: logo, wizard de 5 pasos (Dashboard → Hunter → Ingesta →
 * Visor 3D → Dotación), acciones contextuales y badge de estado de IA.
 *
 * El paso 'ingesta' abre un modal (no es una pantalla), por eso su click
 * dispara `onOpenIngesta` en vez de navegar.
 *
 * Portado de `screens.jsx` (Topbar) del diseño aprobado.
 */

import { Fragment } from 'react';
import type { Screen, WizardStep, DoneState } from '../../types/ui';
import { Icon } from '../Icon';
import { BrandElbow } from '../Brand';

interface WizardStepDef {
  id: WizardStep;
  n: number;
  label: string;
}

const STEPS: readonly WizardStepDef[] = [
  { id: 'dash', n: 1, label: 'Dashboard' },
  { id: 'hunter', n: 2, label: 'Hunter' },
  { id: 'ingesta', n: 3, label: 'Ingesta' },
  { id: 'viewer', n: 4, label: 'Visor 3D' },
  { id: 'dotacion', n: 5, label: 'Dotación' },
] as const;

export interface TopBarProps {
  screen: Screen;
  done: DoneState;
  /** Navega a una pantalla (no 'ingesta', que es modal). */
  onNavigate: (s: Screen) => void;
  onOpenIngesta: () => void;
  onExport: () => void;
}

export function TopBar({
  screen,
  done,
  onNavigate,
  onOpenIngesta,
  onExport,
}: TopBarProps): JSX.Element {
  const currentIdx = STEPS.findIndex((s) => s.id === screen);

  return (
    <div className="topbar">
      <div className="logo">
        <span className="san">SAN</span>
        <BrandElbow className="brand-elbow-svg" gradientId="brand-elbgrad-topbar" />
        <span className="plumb">PLUMB</span>
      </div>
      <div className="sep" />

      <div className="wizard">
        {STEPS.map((s, i) => {
          const isActive = s.id === screen;
          const isDone =
            (s.id === 'ingesta' ? done.ingesta : done[s.id as keyof DoneState]) && !isActive;
          const isPast = currentIdx > i && currentIdx >= 0;

          return (
            <Fragment key={s.id}>
              <button
                className={`wz-step ${isActive ? 'active' : ''} ${isDone || isPast ? 'done' : ''}`}
                onClick={() => {
                  if (s.id === 'ingesta') onOpenIngesta();
                  else onNavigate(s.id as Screen);
                }}
              >
                <span className="wz-circle">{isDone || isPast ? Icon.check : s.n}</span>
                <span className="wz-label">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <span className={`wz-line ${i < currentIdx ? 'done' : ''}`} />
              )}
            </Fragment>
          );
        })}
      </div>

      <div className="topbar-actions">
        {screen === 'viewer' && (
          <>
            <button className="btn ghost" onClick={onOpenIngesta}>
              {Icon.upload} <span>Leer plano</span>
            </button>
            <button className="btn primary" onClick={onExport}>
              {Icon.download} <span>Exportar BOM</span>
            </button>
          </>
        )}
        <span className="ia-status">
          <span className="ia-dot" />
          IS.010 · RNE Perú
        </span>
      </div>
    </div>
  );
}
